import { useEffect, type ReactNode } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/stores/authStore';
import { setAuthErrorCallback } from '@/services/tagsApi';
import { showToast } from '@/components/Toast/Toast';

interface ProtectedRouteProps {
  children: ReactNode;
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, setRedirectUrl } = useAuthStore();

  // Set up the auth error callback for API 401/403 responses
  useEffect(() => {
    setAuthErrorCallback((reason: string) => {
      const message = reason === 'unauthorized' 
        ? t('auth.sessionExpired')
        : t('auth.accessDenied');
      showToast('warning', message);
      navigate('/login', { replace: true });
    });

    // Cleanup on unmount
    return () => {
      setAuthErrorCallback(() => {});
    };
  }, [navigate, t]);

  // If not authenticated, save current location and redirect to login
  if (!isAuthenticated) {
    const currentPath = location.pathname + location.search;
    setRedirectUrl(currentPath);
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}
